import React, { useEffect, useState } from 'react'

import { ActiveSource, Source } from 'types/GlobalSource'
import { CustomEndpoint } from 'types/GlobalCustomEndpoint'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Stack from 'components_new/atoms/Stack'
import Text from 'components_new/atoms/Text'
import TooltipWrapper from 'components_new/atoms/TooltipWrapper'

import SimpleTable from 'components_new/molecules/SimpleTable'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import AddEndpointDialog from './AddEndpointDialog'
import AddEndpointsBulkDialog from './AddEndpointsBulkDialog'
import EndpointAccessCell, { TRowAccessCheck } from './EndpointAccessCell'
import EditEndpointDialog from './EditEndpointDialog'

import * as CustomEndpointActions from 'redux/actions/DataPlatform/connections/CustomEndpoints'
import { checkAccess } from 'redux/api/DataPlatform/connections/CustomEndpoints'
import { useThunkActions } from 'redux/actions/useThunkActions'
import {
  useCustomEndpoints,
  useCustomEndpointsFetching,
  useEntitySetsSourceId
} from 'redux/hooks/CustomEndpoints'

import { getCustomEndpointLabels } from 'utils/sources'

interface SourceEndpointsProps {
  activeSource: ActiveSource
  source: Source
}

const SourceEndpoints = (props: SourceEndpointsProps) => {
  const { activeSource, source } = props

  const endpoints = useCustomEndpoints()
  const fetching = useCustomEndpointsFetching()
  const entitySetsSourceId = useEntitySetsSourceId()

  const tryGetAllCustomEndpoints = useThunkActions(
    CustomEndpointActions.tryGetAllCustomEndpoints
  )
  const tryGetAllDynamicsEntitySets = useThunkActions(
    CustomEndpointActions.tryGetAllDynamicsEntitySets
  )
  const tryCreateCustomEndpoint = useThunkActions(
    CustomEndpointActions.tryCreateCustomEndpoint
  )
  const tryCreateCustomEndpoints = useThunkActions(
    CustomEndpointActions.tryCreateCustomEndpoints
  )
  const tryUpdateCustomEndpoint = useThunkActions(
    CustomEndpointActions.tryUpdateCustomEndpoint
  )
  const tryDeleteCustomEndpoint = useThunkActions(
    CustomEndpointActions.tryDeleteCustomEndpoint
  )

  const [addOpen, setAddOpen] = useState(false)
  const [addManyOpen, setAddManyOpen] = useState(false)
  const [deleteView, setDeleteView] = useState<CustomEndpoint | null>(null)
  /**
   * Per row outcome of asking the source whether the endpoint is readable,
   * keyed by endpoint id. Local rather than redux: it is a point-in-time
   * answer that should not outlive the page.
   */
  const [accessChecks, setAccessChecks] = useState<
    Record<string, TRowAccessCheck>
  >({})
  const [editView, setEditView] = useState<CustomEndpoint | null>(null)

  const labels = getCustomEndpointLabels(source.attributes)
  const canCheckAccess = source.attributes.supports_access_check

  useEffect(() => {
    tryGetAllCustomEndpoints(activeSource.id)
    setAccessChecks({})
  }, [activeSource.id])

  /**
   * Dynamics picks its endpoints from the source's own entity sets, so those
   * have to follow whichever source is open.
   */
  useEffect(() => {
    if (
      source.attributes.is_dynamics &&
      entitySetsSourceId !== activeSource.id
    ) {
      tryGetAllDynamicsEntitySets(activeSource.id)
    }
  }, [activeSource.id, entitySetsSourceId, source.attributes.is_dynamics])

  const handleCheckAccess = (endpoint: CustomEndpoint) => {
    const setCheck = (check: TRowAccessCheck) =>
      setAccessChecks((prev) => ({ ...prev, [endpoint.id]: check }))

    setCheck({ checking: true, result: null })

    checkAccess({
      active_source_id: activeSource.id,
      technical_name: endpoint.attributes.technical_name
    })
      .then((response) =>
        setCheck({ checking: false, result: response.data.data })
      )
      .catch(() =>
        setCheck({
          checking: false,
          result: {
            accessible: false,
            error: 'Åtkomsten kunde inte kontrolleras just nu.'
          }
        })
      )
  }

  if (!labels) {
    return null
  }

  return (
    <Box>
      <SimpleTable
        columns={[
          { title: labels.singular },
          ...(labels.skipCorresponding
            ? []
            : [{ title: labels.corresponding }]),
          ...(canCheckAccess
            ? [{ align: 'right' as const, title: 'Åtkomst', width: 96 }]
            : []),
          {
            align: 'right' as const,
            width: labels.skipCorresponding ? 56 : 96
          }
        ]}
        addRow={{ onClick: () => setAddOpen(true) }}
        loading={fetching}
        rows={endpoints.map((endpoint) => ({
          cells: [
            endpoint.attributes.technical_name,
            ...(labels.skipCorresponding
              ? []
              : [endpoint.attributes.table_name]),
            ...(canCheckAccess
              ? [
                  <EndpointAccessCell
                    check={accessChecks[endpoint.id]}
                    key="access"
                    onCheck={() => handleCheckAccess(endpoint)}
                  />
                ]
              : []),
            <Stack direction="row" justifyContent="flex-end" key="actions">
              {labels.skipCorresponding ? null : (
                <TooltipWrapper title={`Byt ${labels.corresponding.toLowerCase()}`}>
                  <IconButton onClick={() => setEditView(endpoint)} size="small">
                    <Icon fontSize="small" name="Edit" />
                  </IconButton>
                </TooltipWrapper>
              )}
              <TooltipWrapper title="Ta bort">
                <IconButton
                  onClick={() => setDeleteView(endpoint)}
                  size="small"
                >
                  <Icon fontSize="small" name="RemoveCircleOutlineOutlined" />
                </IconButton>
              </TooltipWrapper>
            </Stack>
          ],
          id: endpoint.id
        }))}
        size="small"
      />

      {!fetching && endpoints.length === 0 ? (
        <Text color="text.secondary" sx={{ mt: 2 }} variant="body2">
          {`Inga ${labels.plural} är upplagda för den här källan än.`}
        </Text>
      ) : null}

      <AddEndpointDialog
        activeSourceId={activeSource.id}
        canCheckAccess={canCheckAccess}
        labels={labels}
        onClose={() => setAddOpen(false)}
        onSubmit={(body, callback) =>
          tryCreateCustomEndpoint(
            { ...body, active_source_id: activeSource.id },
            callback
          )
        }
        onSwitchToMany={() => setAddManyOpen(true)}
        open={addOpen}
      />

      <AddEndpointsBulkDialog
        labels={labels}
        onClose={() => setAddManyOpen(false)}
        onSubmit={(endpoints, callback) =>
          tryCreateCustomEndpoints(
            { active_source_id: activeSource.id, endpoints },
            callback
          )
        }
        open={addManyOpen}
      />

      <EditEndpointDialog
        endpoint={editView}
        labels={labels}
        onClose={() => setEditView(null)}
        onSubmit={(tableName, callback) => {
          if (!editView) {
            return
          }

          tryUpdateCustomEndpoint(
            editView.id,
            { table_name: tableName },
            callback
          )
        }}
      />

      <DeleteDialog
        onClose={() => setDeleteView(null)}
        onDelete={(cb) => {
          if (!deleteView) {
            return
          }

          tryDeleteCustomEndpoint(deleteView.id, (err) => {
            if (!err) {
              setDeleteView(null)
            }
            cb()
          })
        }}
        open={Boolean(deleteView)}
        title={deleteView?.attributes.technical_name ?? ''}
        type={labels.singular.toLowerCase()}
      />
    </Box>
  )
}

export default SourceEndpoints
